import { NumberOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { CreditCardIcon } from 'assets/images/svg';
import { CREDENTIAL_TYPE_PASSCODE } from 'config/credentialTypes';
import { groupCredentialsBySourceForDevice } from 'library/helpers/accessControl';
import React, { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { selectCredentials, selectLoadingCredentialIDs } from 'reduxStore/credentials/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectPermissionsByDeviceID } from 'reduxStore/permissions/selectors';
import { Empty, Flex, Spin, Tag, Tooltip } from 'zui/Antd';

const CredentialPanelContent = ({ deviceID }) => {
	// Hooks
	const {
		token: { fontWeightStrong, paddingSM, paddingXXS }
	} = theme.useToken();

	// ✅ Redux state
	const permissions = useSelector((state) => selectPermissionsByDeviceID(state, deviceID));
	const credentials = useSelector(selectCredentials);
	const people = useSelector(selectPeople);
	const loadingCredentialIDs = useSelector(selectLoadingCredentialIDs);
	const credentialPropagation = useSelector((state) => state.Credentials.credentialPropagation, shallowEqual);

	// Build display items — keep credentials with active loading/propagation visible
	const displayItems = useMemo(() => {
		const items = groupCredentialsBySourceForDevice({ permissions, credentials, people });
		const activeIDs = new Set([
			...Object.keys(loadingCredentialIDs).map(Number),
			...Object.keys(credentialPropagation).map(Number)
		]);
		// Add credentials that are loading/propagating but not in the permission-based list
		const inFlight = [...activeIDs]
			.filter((id) => !items.some((item) => item.credentialID === id))
			.map((id) => {
				const cred = credentials.find((c) => c.credentialID === id);
				if (!cred) return null;
				const person = people.find((p) => p.personID === cred.personID);
				return {
					...cred,
					key: `inflight-${id}`,
					tooltip: person?.displayName || 'Propagating...'
				};
			})
			.filter(Boolean);
		return [...items, ...inFlight];
	}, [permissions, credentials, people, loadingCredentialIDs, credentialPropagation]);

	return (
		<Flex wrap gap={paddingSM}>
			{displayItems.length === 0 ? (
				<div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<Empty description='No credentials' image={Empty.PRESENTED_IMAGE_SIMPLE} />
				</div>
			) : (
				displayItems.map((credential) => {
					const isPasscode = credential.credentialTypeID === CREDENTIAL_TYPE_PASSCODE;
					const isLoading = !!loadingCredentialIDs[credential.credentialID];

					return (
						<Flex key={credential.key} vertical>
							<Tooltip title={credential.tooltip || ''} placement='top'>
								<Spin spinning={isLoading} size='small'>
									<Tag
										color='magenta'
										style={{
											fontWeight: fontWeightStrong,
											borderColor: 'currentColor',
											marginInlineEnd: 0,
											opacity: isLoading ? 0.5 : 1
										}}
									>
										<Flex align='center' gap={paddingXXS}>
											{isPasscode ? (
												<>
													<NumberOutlined />
													{credential.name} - {credential.value}
												</>
											) : (
												<>
													<CreditCardIcon color='currentColor' />
													{credential.name} - {credential.value}
												</>
											)}
										</Flex>
									</Tag>
								</Spin>
							</Tooltip>
						</Flex>
					);
				})
			)}
		</Flex>
	);
};

export default CredentialPanelContent;
